import { useEffect, useState } from 'react';

import { supabase } from '@/renderer/services/supabase';
import { isElectron } from '@/renderer/util';

function decodeJwtPayload(token: string): { iat: number; exp: number } | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      iat: number;
      exp: number;
    };
  } catch {
    return null;
  }
}

/**
 * Detects whether the Electron device's system clock is significantly out of
 * sync with the Supabase server clock.
 *
 * Only active when running inside Electron — the web build has no access to
 * the `window.electron` IPC bridge and cannot fix OS clock settings.
 *
 * Detection strategy:
 *  - getSession() returns the current valid JWT (refreshed if needed).
 *  - System time comes from the Electron main process via IPC ('system:getTime')
 *    for a Node.js / OS-level reading rather than the Chromium renderer clock.
 *  - A valid token satisfies: iat ≤ now ≤ exp (server perspective).
 *    If the local clock falls outside those bounds by more than the grace
 *    periods, the device clock is skewed:
 *      • clockBehind: systemNow < iat − 60s  (>1 min before issuance)
 *      • clockAhead : systemNow > exp + 300s (>5 min past expiry)
 */
export function useClockSkewDetection(): boolean {
  const [isSkewed, setIsSkewed] = useState(false);

  useEffect(() => {
    // Web users: no Electron IPC available and no way to fix OS clock from
    // a browser tab — skip the check entirely.
    if (!isElectron()) return undefined;

    let cancelled = false;

    const check = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token || cancelled) return;

        const payload = decodeJwtPayload(session.access_token);
        if (!payload) return;

        // Get system clock from the Electron main process (Node.js / OS).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const systemNowMs = (await (window as any).electron.ipcRenderer.invoke(
          'system:getTime',
        )) as number;
        const systemNow = Math.floor(systemNowMs / 1000);

        // Session is server-valid. If local clock falls outside the token's
        // [iat, exp] window by more than the grace periods, the clock is off.
        const clockBehind = systemNow < payload.iat - 60; // >1 min behind issuance
        const clockAhead = systemNow > payload.exp + 300; // >5 min past expiry

        if (!cancelled) {
          setIsSkewed(clockBehind || clockAhead);
        }
      } catch {
        // No session or IPC unavailable — do not warn
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  return isSkewed;
}
